import { ApolloError, useMutation, useQuery } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    FirmId,
    FirmResponse,
} from '@deltek/specification-client/core/Models/firm'
import { nameValuePropType } from '@deltek/specification-client/core/Models/nameValuePropType'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { FormControl, TextField } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import clsx from 'clsx'
import { ProjectGroupInput } from 'models/projectGroupInput'
import React, { ChangeEvent } from 'react'
import {
    SaveProjectGroupMutation,
    UpdateProjectGroupMutation,
} from '../../../../api/graphQL/mutations/projectGroup.mutation'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingTop: theme.spacing(1.875),
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(3.125),
        },
    },
    dialogCont: {
        color: theme.palette.common.black,
        width: 630,
        height: 87,
        fontSize: pxToRem(18),
    },
    actionButton: {
        color: '#A78966',
        fontWeight: 400,
        fontSize: pxToRem(18),
        lineHeight: pxToRem(40),
        letterSpacing: pxToRem(0.25),
        '&.MuiButton-text:last-child': {
            marginRight: theme.spacing(3),
        },
    },
    confirmationText: {
        paddingTop: theme.spacing(2),
        fontSize: pxToRem(18),
        color: theme.palette.common.black,
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    projectGroupTextField: {
        '& label': {
            '& span': {
                '&.MuiInputLabel-asterisk': {
                    color: 'red',
                },
            },
        },
    },
    projectGroupDialogContainer: {
        '&.projectGroupDialogPosition': {
            zIndex: '1305 !important',
        },
    },
}))

export interface ProjectGroupDialogProps {
    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose: VoidFunction

    /**
     * Is project group edit
     */
    groupName?: string

    /**
     * Refetch data on save
     */
    refetch: VoidFunction
}
const ProjectGroupDialog: React.FC<ProjectGroupDialogProps> = ({
    openDialog,
    handleDialogClose,
    refetch,
    groupName,
}) => {
    const classes = useStyles()
    const [projectGroupName, setProjectGroupName] = React.useState<String>('')
    const [errorMessage, setErrorMessage] = React.useState<String>('')
    const [hasError, setHasError] = React.useState<boolean>(false)
    const [projectGroupsDialog, setProjectGroupsDialog] = React.useState<
        Record<nameValuePropType, string>[]
    >([])
    const [groupId, setGroupId] = React.useState<string>('')

    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const firmId =
        currentUserInfoResponse?.currentUserInfo?.account?.accountId ?? ''

    const [textfieldref, setTextFieldRef] = React.useState<
        React.RefObject<HTMLElement> | null | HTMLElement
    >(null)

    const isEdit = React.useMemo(() => {
        return groupName?.length ?? 0 > 0
    }, [])

    const [isFormDirty, setIsFormDirty] = React.useState<boolean>(false)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const {} = useQuery<FirmResponse, FirmId>(FirmProjectParametersQuery, {
        variables: {
            firmId: firmId ?? '',
        },
        onCompleted: (data) => {
            const projectGroups = (data.firm.projectGroups || []).map(
                (projectGroup) => {
                    return {
                        value: projectGroup.id!,
                        name: projectGroup.name!,
                    } as Record<nameValuePropType, string>
                }
            )
            setProjectGroupsDialog(projectGroups)
        },
    })

    const handleProjectGroupNameChange = (
        e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        setHasError(false)
        setIsFormDirty(true)
        setErrorMessage('')
        setProjectGroupName(e.currentTarget.value)
    }

    const handleProjectGroupNameSave = () => {
        if (projectGroupName.length > 0 && firmId) {
            const obj: ProjectGroupInput = {
                id: isEdit ? groupId : undefined,
                firmId: firmId,
                name: projectGroupName,
            }

            if (isEdit) {
                updateProjectGroup({
                    variables: {
                        projectGroupInput: obj,
                    },
                })
            } else {
                createProjectGroup({
                    variables: {
                        projectGroupInput: obj,
                    },
                })
            }
        } else {
            setHasError(true)
            setErrorMessage('Project Group Name required')
        }
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        handleProjectGroupNameSave()
    }

    const [createProjectGroup] = useMutation<{
        projectGroupInput: ProjectGroupInput
    }>(SaveProjectGroupMutation, {
        onCompleted: () => {
            showGlobalSnackbar({
                variables: {
                    message: `Project group created successfully.`,
                    variant: 'success',
                    id: `ProjectHomePage_ProjectGroupCreateSuccess`,
                },
            })
            handleDialogClose()
            refetch()
        },
        onError: (error: ApolloError) => {
            setErrorMessage(error.message)
            setHasError(true)
        },
    })

    const [updateProjectGroup] = useMutation<{
        projectGroupInput: ProjectGroupInput
    }>(UpdateProjectGroupMutation, {
        onCompleted: () => {
            showGlobalSnackbar({
                variables: {
                    message: `Project group edited successfully.`,
                    variant: 'success',
                    id: `ProjectHomePage_ProjectGroupEditSuccess`,
                },
            })
            handleDialogClose()
            /*
             *   Added timeout to compensate for delay in updating of project groups on the API side
             */
            setTimeout(() => {
                refetch()
            }, 1000)
        },
        onError: (error: ApolloError) => {
            setErrorMessage(error.message)
            setHasError(true)
        },
    })

    const getId = (groupName: String) => {
        if (groupName && projectGroupsDialog) {
            projectGroupsDialog.forEach((el) => {
                el.name === groupName && setGroupId(el.value)
            })
        }
    }

    React.useEffect(() => {
        if (openDialog) {
            setProjectGroupName(groupName ?? '')
            setErrorMessage('')
            setIsFormDirty(false)
            setHasError(false)
        }
    }, [openDialog, groupName])

    React.useEffect(() => {
        if (groupName && projectGroupsDialog) {
            setProjectGroupName(groupName)
            getId(groupName)
        }
    }, [groupName, projectGroupsDialog])

    React.useEffect(() => {
        if (textfieldref) {
            setTimeout(() => {
                ;(textfieldref as HTMLElement).focus()
            }, 0)
        }
    }, [textfieldref])

    return (
        <Dialog
            className={clsx(
                classes.projectGroupDialogContainer,
                'projectGroupDialogPosition'
            )}
            open={openDialog}
            data-testid="projectGroupAddDialog"
            aria-labelledby="add-project-group-properties"
        >
            <DialogTitle className={classes.dialogTitle}>
                {isEdit ? 'Edit Project Group Name' : 'Create Project Group'}
            </DialogTitle>
            <DialogContent className={classes.dialogCont}>
                <form onSubmit={(e) => handleSubmit(e)}>
                    <FormControl fullWidth variant="outlined">
                        <TextField
                            inputRef={setTextFieldRef}
                            data-testid="projectGroupNameField"
                            label={'Name Project Group'}
                            disabled={false}
                            name="projectRole"
                            value={projectGroupName}
                            placeholder={'Enter Name Here'}
                            required={true}
                            error={hasError}
                            helperText={hasError && errorMessage}
                            onChange={(event) => {
                                handleProjectGroupNameChange(event)
                            }}
                            inputProps={{
                                maxLength: 100,
                                'aria-autocomplete': 'none',
                                autoComplete: 'chrome-off',
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            className={classes.projectGroupTextField}
                        />
                    </FormControl>
                </form>
            </DialogContent>
            <DialogActions className={classes.dialogAction}>
                <Button
                    data-testid="cancelAddButton"
                    className={classes.actionButton}
                    onClick={handleDialogClose}
                >
                    Cancel
                </Button>
                <Button
                    disabled={projectGroupName.length === 0 || !isFormDirty}
                    data-testid="saveAddButton"
                    onClick={handleProjectGroupNameSave}
                    className={classes.actionButton}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ProjectGroupDialog
